import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ILocale } from '../../../models/i18n.model'
import { doGetLocales } from '../../../store/locale.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import qs from 'qs';

import { useSnackbar } from 'notistack';
import { Select, Table, TableColumnsType } from 'antd';


import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Pagination from '../../../components/Pagination';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import config from '../../../lib/config';
import { errorVariant, warningVariant } from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';
const { Option } = Select;

const SystemLocale: FC = () => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { locales, isLoading } = useSelector((state: RootState) => state.locale);
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { enqueueSnackbar } = useSnackbar();

  const [localeData, setLocaleData] = useState<ILocale[]>([]);
  const [localeCount, setLocaleCount] = useState(0);
  const [page, setPage] = useState<number>(query.page ? Number(query.page) : 1);
  const [field, setField] = useState<any>();
  const [keyword, setKeyword] = useState('');
  const [param, setParam] = useState({});

  const fieldList = [
    { label: t('system.locale.column2'), value: 'localeSymbol' },
    { label: t('system.locale.column3'), value: 'korName' },
    { label: t('system.locale.column4'), value: 'engName' }
  ];
  
  useEffect(() => {
    setField('localeSymbol');
    getLocales();
  }, []);

  useEffect(() => {
    // query to state
    const _param: any = {};

    if (query.page) {
      setPage(Number(query.page));
      _param.page = query.page;
    }
    if (query.field) {
      setField(String(query.field));
      _param.field = query.field;
    }
    if (query.keyword) {
      setKeyword(String(query.keyword));
      _param.keyword = query.keyword;
    }

    setParam(_param);

    // data filtering
    if (locales) {
      let data: ILocale[] = locales;

      if (query.field && query.keyword) {
        data = data.filter(code => code[String(query.field)] && code[String(query.field)].toLowerCase().indexOf(String(query.keyword).toLowerCase()) > -1);
      }
      
      setLocaleCount(data.length);
      setLocaleData(data);
    }
  }, [location, locales]);

  const getLocales = async () => {
    const res = await dispatch(doGetLocales());

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      case 404:
        enqueueSnackbar(t('common.message.notFound'), warningVariant);
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onSearch = () => {
    const param: any = { page: 1 };

    if (field && keyword) {
      param.field = field;
      param.keyword = keyword;
    }

    setParam(param);
    history.push(`/system/locale?${qs.stringify(param)}`);
  }

  const columns: TableColumnsType<ILocale> = [
    {
      title: t('system.locale.column2'),
      dataIndex: 'localeSymbol',
    },
    {
      title: t('system.locale.column3'),
      dataIndex: 'korName',
    },
    {
      title: t('system.locale.column4'),
      dataIndex: 'engName',
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-system-big.svg"
      parentMenu={t('common.menu.system')}
      currentMenu={t('system.locale.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <div>
              <Select
                value={field}
                onChange={value => setField(value)}
              >
                {
                  fieldList?.map((item: any) => {
                    return (
                      <Option
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </Option>
                    )
                  })
                }
              </Select>
            </div>
            <input
              type="text"
              className="form-control w-240p"
              value={keyword}
              onChange={(e => setKeyword(e.target.value))}
              onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
            />
            <div className="comm-btn__area">
              <Button
                className='btn-basic small black'
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>	
    <div className="content scrollbar">
      {isLoading && <Loader />}

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{localeCount}</b>{t('common.text.unit')}
          </div>
          <div className="comm-btn__area">
            <Button
              className='btn-basic pink'
              onClick={() => history.push(`/system/locale/create?${qs.stringify(param)}`)}
              text={t('common.button.create')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className="comm-table__area">
          <Table
            columns={columns}
            dataSource={localeData || undefined}
            pagination={{
              current: page,
              pageSize: config.pageSize
            }}
            onRow={record => {
              return {
                onClick: () => {
                  history.push(`/system/locale/${record.localeSymbol}/edit?${qs.stringify(param)}`);
                }
              };
            }}
            rowClassName="pointer"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={localeCount}
              page={page}
              param={param}
              path="/system/locale"
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className='btn-basic pink'
              onClick={() => history.push(`/system/locale/create?${qs.stringify(param)}`)}
              text={t('common.button.create')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
    </>
  )
}

export default SystemLocale;